/**
 * Tenant-wide labels store — tags with color + emoji that can be attached
 * to tasks. Kept in a separate slice from `tasksStore` because labels are
 * loaded independently and consumed by multiple pages (TasksHub, TaskDialog,
 * future filter chips).
 *
 * Data shape (from `/api/labels`):
 *   { id, tenant_id, name, color, emoji, created_by, created_at }
 */
import { create } from "zustand";
import { api } from "../lib/api";

export const useLabelsStore = create((set, get) => ({
  labels: [],
  loading: false,
  loaded: false,

  /** Fetch labels once per session; subsequent callers get the cached list. */
  async fetchLabels(force = false) {
    if (get().loaded && !force) return get().labels;
    set({ loading: true });
    try {
      const { data } = await api.get("/labels");
      set({ labels: data || [], loading: false, loaded: true });
      return data || [];
    } catch (err) {
      set({ loading: false });
      throw err;
    }
  },

  async createLabel(payload) {
    const { data } = await api.post("/labels", payload);
    set((s) => {
      // POST is idempotent server-side (dup name returns the existing row).
      const already = s.labels.find((l) => l.id === data.id);
      if (already) return s;
      return { labels: [...s.labels, data].sort((a, b) => a.name.localeCompare(b.name, "tr")) };
    });
    return data;
  },

  async updateLabel(labelId, patch) {
    const { data } = await api.patch(`/labels/${labelId}`, patch);
    set((s) => ({
      labels: s.labels
        .map((l) => (l.id === labelId ? { ...l, ...data } : l))
        .sort((a, b) => a.name.localeCompare(b.name, "tr")),
    }));
    return data;
  },

  async deleteLabel(labelId) {
    await api.delete(`/labels/${labelId}`);
    set((s) => ({ labels: s.labels.filter((l) => l.id !== labelId) }));
  },
}));
